import Sentry from "@similarweb/sentry-logger";

import {
    receiveSegmentsDashboardModuleMeta,
    receiveSegmentsModuleMeta,
    requestSegmentsModuleMeta,
    setEnabledWidgets,
} from "app/actions/segmentsModuleActions";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import {
    getDurationSetupForSegmentsGroup,
    getValidDurationBySetup,
} from "app/pages/segments/analysis/SegmentsAnalysisHelper";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import type { ICustomSegmentsMetaData, WidgetAvailability } from "app/services/segments/segmentsApiService";
import segmentsApiService from "app/services/segments/segmentsApiService";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export const hasSegmentsClaim = () => isAvailable(swSettings.components.CustomSegments);
export const hasCustomLobsSegmentsClaim = () => isAvailable(swSettings.components.CustomLobsSegments);
export function checkDurationIsValid() {
    const currentRouteState = proNavigator.current();
    const { duration } = proNavigator.getParams();
    return (currentRouteState as any).overrideDurationFilterParams?.validDuration === duration;
}

export const loadCustomSegmentsMetadataWrapper = (forceLoad = false, timeout = undefined) => {
    if (!hasSegmentsClaim()) {
        return;
    }
    loadCustomSegmentsMetadata(forceLoad, timeout);
};

export async function loadCustomSegmentsMetadata(forceLoad = false, timeout = undefined) {
    try {
        const swRedux = SwRedux.getInstance();
        const { segmentsModule } = swRedux.getState();

        if (!forceLoad && segmentsModule.customSegmentsMeta) {
            return true;
        }

        swRedux.dispatch(requestSegmentsModuleMeta());
        await new segmentsApiService()
            .getUserCustomSegmentsAndSegmentGroups(timeout)
            .then(setCustomSegmentsMeta)
            .catch((error) => {
                Sentry.error("Error when fetching custom segments meta data", error);
            });
        return true;
    } catch (e) {
        return true;
    }
}

export const setCustomSegmentsMeta = (customSegmentsMeta: FixmeType) => {
    if (!customSegmentsMeta) {
        return;
    }
    const swRedux = SwRedux.getInstance();
    const FilteredAccountSegments = customSegmentsMeta.AccountSegments?.filter(
        (segment: FixmeType) => !segment.isLocked && !itsMyOwnSegment(segment.userId),
    );
    customSegmentsMeta.FilteredAccountSegments = FilteredAccountSegments;
    swRedux.dispatch(receiveSegmentsModuleMeta(customSegmentsMeta));
};

export async function getCustomSegmentsMetadata() {
    const swRedux = SwRedux.getInstance();
    swRedux.dispatch(requestSegmentsModuleMeta());

    try {
        const customSegmentsMeta = await new segmentsApiService().getUserCustomSegmentsAndSegmentGroups();

        swRedux.dispatch(receiveSegmentsModuleMeta(customSegmentsMeta));
        return customSegmentsMeta;
    } catch (error) {
        Sentry.error(
            "Error when fetching custom segments meta data [template/wizard autocomplete or dashboards]",
            error,
        );
        return {} as ICustomSegmentsMetaData;
    }
}

export async function loadCustomSegmentsMetadataForDashboard(dashboardId: string) {
    try {
        const swRedux = SwRedux.getInstance();

        await new segmentsApiService().getCustomSegmentsForDashboard(dashboardId).then((dashboardSegmentsMeta) => {
            // @ts-ignore FIXME: strict migration - Argument of type 'unknown' is not assignable to parameter of type 'DashboardSegmentsMeta'
            swRedux.dispatch(receiveSegmentsDashboardModuleMeta(dashboardSegmentsMeta));
        });

        return true;
    } catch (error) {
        Sentry.error("Error when fetching Custom Segments for dashboard", error);
        return true;
    }
}

export async function getEnabledWidgets(dashboardId: string) {
    try {
        const swRedux = SwRedux.getInstance();

        await new segmentsApiService().getDashboardEnabledWidgets(dashboardId).then((enabledWidget) => {
            const result = {} as WidgetAvailability;

            // @ts-ignore FIXME: strict migration - Property 'forEach' does not exist on type 'unknown'
            enabledWidget.forEach((widget) => {
                result[widget.Id] = {
                    id: widget.Id,
                    isEnabled: widget.IsEnabled,
                };
            });
            swRedux.dispatch(setEnabledWidgets(result));
        });
    } catch (error) {
        Sentry.error("Error when fetching Enabled Widgets for dashboard", error);
    }
}

export function resolveOverrideDurationFilterParams(state: FixmeType, params: FixmeType) {
    const swRedux = SwRedux.getInstance();

    const {
        segmentsModule: { customSegmentsMeta },
    } = swRedux.getState();
    // get current segments group viewed
    const segmentsGroup =
        params.mode === MODE.group
            ? customSegmentsMeta.SegmentGroups.find((grp: FixmeType) => grp.id === params.id)
            : undefined;
    if (segmentsGroup) {
        // if viewing segments group, then get its duration filter setup and valid duration and resolve overridden duration filter params
        const groupDurationSetup = getDurationSetupForSegmentsGroup(
            segmentsGroup,
            swSettings.components[
                state.configId || state?.childStates[params.webSource.toLowerCase()]?.configId || "CustomSegments"
            ],
            params,
        );
        const validDuration = getValidDurationBySetup(groupDurationSetup, params.duration);
        return {
            validDuration,
            validComparedDuration: "",
            isDurationDisabled: !groupDurationSetup.durationEnabled,
            overrideDatepickerPresets: groupDurationSetup.allowedPresets,
            minDate: groupDurationSetup.minDate,
            maxDate: groupDurationSetup.maxDate,
        };
    }
}
